import { Component, OnChanges, OnDestroy, Input } from "@angular/core";
import {
    ITCIRating,
    ITCIMarketplaceListing,
    ITCIRatingComment,
    ITCIRatingScore,
    TCIRatingScore,
    TCI_RATINGS_COMPONENT_MODE,
    TCI_RATING_MODEL,
    ITCIMarketplaceV2LikeResponse,
    ITCILike,
    TCI_MARKETPLACE_NOTIFICATION_TYPE
} from "@tci/common";
import { Subscription } from "rxjs";
import { BoxAlignment } from "../../../ng-shared/ng-components/ng-popup/ng-popup.component";
import { NgMarketplaceV2ListingService } from "../ng-marketplace-v2-listing.service";
import { NgMarketplaceV2RatingsService } from "./ng-marketplace-v2-ratings.service";
import { NgMarketplaceV2Service } from "../../ng-marketplace-v2.service";
const noLikesText = "No likes yet";
const likesExistText = " users like this listing";
const userLikedText = "You’ve already liked this listing";
const firstToLIkeText = "Be the first to like this listing";

@Component({
    selector: "ng-marketplace-v2-ratings",
    templateUrl: "./ng-marketplace-v2-ratings.component.html",
    styleUrls: ["./ng-marketplace-v2-ratings.component.less"]
})
export class NgMarketplaceV2RatingsComponent implements OnChanges, OnDestroy {
    @Input() asset: ITCIMarketplaceListing;
    // Ratings Display Mode - Like | Rating
    @Input() ratingModel: TCI_RATING_MODEL = TCI_RATING_MODEL.Like;
    // Component Mode Being Inserted in - LIST_FILTER_SELECT | LIST_READ_ONLY | DETAIL_READ_ONLY | DETAIL_EDIT
    @Input() mode: TCI_RATINGS_COMPONENT_MODE = TCI_RATINGS_COMPONENT_MODE.LIST_READ_ONLY;
    readonly TCI_RATING_MODEL = TCI_RATING_MODEL;
    readonly TCI_RATINGS_COMPONENT_MODE = TCI_RATINGS_COMPONENT_MODE;

    modeRO = TCI_RATINGS_COMPONENT_MODE.DETAIL_READ_ONLY;
    modeEdit = TCI_RATINGS_COMPONENT_MODE.DETAIL_EDIT;
    alignment = BoxAlignment.BOTTOM_LEFT;

    allRatings: ITCIRating;
    userRating = TCIRatingScore.ZERO;
    userComments: ITCIRatingComment;
    userCommentsText: string; // From textarea

    // TODO: Add some @Output to tell what's going on

    showPopup: boolean;
    isBusy = false;
    hasError = false;

    isAssetEnabled: boolean;
    isUpdatedRating: boolean; // User ratings have been submited time ago
    submitUserRating: boolean; // User has just submited the ratings
    private isUpdating = false; // Ratings submission is for updating

    // Likes config
    connectorId: string;
    likes: number;
    isLiked = false;
    isShowLikeDialog = false;
    likeStatus = noLikesText;
    subscriptions: Subscription[] = [];

    get isLikesBusy(): boolean {
        return this.mkRatingsService.isUpdating;
    }

    constructor(
        private mktSvc: NgMarketplaceV2Service,
        private mktListingSvc: NgMarketplaceV2ListingService,
        private mkRatingsService: NgMarketplaceV2RatingsService
        ) {
        this.init();
        this.subscriptions.push(
            // Update the like count in the asset binded to this component
            // To maintain the likes state everytime it changes from the detail view
            mkRatingsService.getLikesSubject()
                .filter(val => this.asset !== undefined && val.connectorId === this.asset.id)
                .subscribe(status => {
                    this.likes = status.likes;
                    if (this.asset && this.asset.likes && this.mode.includes("LIST")) {
                        this.asset.likes.likeCount = this.likes;
                        this.mktSvc.notifyChange({ listing: this.asset, type: TCI_MARKETPLACE_NOTIFICATION_TYPE.Update });
                    }
                })
        );
    }

    ngOnChanges() {
        this.init();
        if (this.ratingModel === TCI_RATING_MODEL.Rating) {
            this.getRatings();
        } else if (this.mode === TCI_RATINGS_COMPONENT_MODE.DETAIL_EDIT && this.ratingModel === TCI_RATING_MODEL.Like) {
            this.getLikeData();
        }
    }

    private init() {
        this.showPopup = false;
        this.hasError = false;
        this.isAssetEnabled = this.asset && this.asset.enabled;
        this.initUserRatings();

        if (this.asset && this.asset.ratings) {
            this.allRatings = this.asset.ratings;
        } else {
            this.allRatings = { r1_count: 0, r2_count: 0, r3_count: 0, r4_count: 0, r5_count: 0};
        }
        // Likes Init
        this.likeStatus = "";
        this.connectorId = this.asset && this.asset.id || "";
        if (this.asset && this.asset.likes) {
            this.likes = this.asset.likes.likeCount;
        } else {
            this.likes = 0;
        }
    }

    ngOnDestroy() {
        this.subscriptions.forEach(s => s.unsubscribe());
    }

    private initUserRatings() {
        this.userRating = TCIRatingScore.ZERO;
        delete this.userComments;
        delete this.userCommentsText;
        this.submitUserRating = false;
        this.isUpdatedRating = false;
    }

    // Ratings
    private getRatings() {
        this.isBusy = true;
        this.mktListingSvc.getRatings(this.asset)
            .do(() => this.isBusy = false)
            .subscribe(res => {
                if (res.success) {
                    if (res.data) {
                        this.userRating = TCIRatingScore.getRatingFromNumber(res.data.val);
                        this.userComments = res.data;
                        this.isUpdatedRating = true;
                        this.isUpdating = true;
                    } else { // User has not added ratings
                        this.initUserRatings();
                        this.isUpdating = false;
                    }
                } else {
                    this.hasError = true;
                }
            });
    }

    submitRating() {
        this.isBusy = true;
        const ratings: ITCIRatingComment = {
            val: this.userRating.getRatingValue(),
            comment: this.userCommentsText,
            comment_dt: new Date().getTime()
        };

        this.mktListingSvc.postRatings(this.asset, ratings, this.isUpdating)
            .do(() => this.isBusy = false)
            .subscribe(res => {
                if (res.success) {
                    this.submitUserRating = true;
                    this.isUpdating = true;
                    this.userRating = TCIRatingScore.getRatingFromNumber(res.data.val);
                } else {
                    this.hasError = true;
                }
            });

    }

    showRatingEdit(isUpdatedRating: boolean) {
        this.showPopup = true;
        if (this.submitUserRating) { // Rating was just submited´
            this.isUpdatedRating = true;
        } else {
            this.isUpdatedRating = isUpdatedRating;
        }
        this.submitUserRating = false;
    }

    closeRatingPopover() {
        this.showPopup = false;
    }

    storeUserRatings(ratings: ITCIRatingScore) {
        this.userRating = TCIRatingScore.getRatingFromNumber(ratings.val);
    }

    // Like CRUD Logic should be handled by this wrapper component
    // Like Methods

    toggleLikeDialog(show = true, e?: any) {
        if (e) {
            e.stopPropagation();
        }
        if (!show) {
            this.isShowLikeDialog = false;
            this.likeAreaExited();
        } else {
            this.isShowLikeDialog = true;
        }
    }

    getLikeData() {
            this.mkRatingsService
            .getLikeById(this.connectorId)
            .subscribe((res: ITCIMarketplaceV2LikeResponse) => {
                if (res.success) {
                    this.isLiked = res.data.val ? res.data.val : false;
                } else {
                    this.handleError(`Failed Get User Likes for the connector ${this.connectorId}`);
                }
                this.likeStatusCheck();
            }, err => this.handleError(err));
    }

    handleError(err) {
        console.log("Ratings Error: ", err);
    }

    likeAreaHovered() {
        if (this.likes === 0 && !this.isShowLikeDialog) {
            this.likeStatus = firstToLIkeText;
        }
    }
    likeAreaExited() {
        if (this.likes === 0 && !this.isShowLikeDialog) {
            this.likeStatus = noLikesText;
        }
    }
    likeToggle(val = false) {
        // Comment should be NA until we have comment box UI for adding it.
        const likeData: ITCILike = { val, comment: "NA" };
            this.mkRatingsService
                .createLikebyId(this.isLiked, this.asset, likeData)
                .subscribe((result: {val: boolean; id: string}) => {
                    if (this.connectorId === result.id) {
                        this.isLiked = result.val;
                        this.likeStatusCheck();
                    }
                }, err => this.handleError(err));
        this.isShowLikeDialog = false;
    }

    likeStatusCheck() {
        if (this.likes) {
            this.likeStatus = this.isLiked ? userLikedText : this.likes + likesExistText;
        } else {
            const noLikeDialogText = this.isShowLikeDialog ? firstToLIkeText : noLikesText;
            this.likeStatus = this.isLiked ? userLikedText : noLikeDialogText;
        }
    }

}
